import React, { RefObject, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { FormValidationState } from 'utils';

import { AutocompleteAddressInput } from 'components/autocomplete-address-input';
import { IPlaceAddress } from 'utils/geolocation';

import { IPhoneAddressValidation } from '../types';

const DeliveryAddressWrapper = styled.div`
  position: relative;
  margin: 1.5rem 0 1rem;
`;

const DeliveryAddressInput = styled(AutocompleteAddressInput)`
  z-index: ${Styles.zIndex.below};
`;

interface IDeliveryAddressFormInputProps {
  addressString: string;
  deliveryAddressInputRef?: React.RefObject<HTMLInputElement>;
  message: string;
  setAddressComponents: (address: IPlaceAddress | null) => void;
  setAddressString: (address: string) => void;
  setCoordinates: (coordinates: undefined) => void;
  setMessage: (message: string) => void;
  setPlaceId: (placeId: string) => void;
  setValidation: (validation: IPhoneAddressValidation) => void;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  unitRef: RefObject<HTMLInputElement>;
  validation: IPhoneAddressValidation;
}

const DeliveryAddressFormInput: React.FC<IDeliveryAddressFormInputProps> = ({
  setAddressString,
  setPlaceId,
  setValidation,
  setMessage,
  unitRef,
  validation,
  setCoordinates,
  setAddressComponents,
  addressString,
  deliveryAddressInputRef,
  message,
  setValue,
}) => {
  const { formatMessage } = useIntl();

  const defaultAddressInputRef = useRef<HTMLInputElement>(null);

  const AddressInputRef = deliveryAddressInputRef ?? defaultAddressInputRef;

  const handleAddressChange = (newAddressString: string, id: string) => {
    setAddressString(newAddressString);
    setMessage('');
    if (!id) {
      setValidation({
        ...validation,
        address: FormValidationState.INVALID,
      });
      setMessage(formatMessage({ id: 'addressError' }));
      setAddressComponents(null);
      return;
    }

    if (unitRef.current) {
      unitRef.current.focus();
    }

    setValidation({
      ...validation,
      address: FormValidationState.PRISTINE,
    });
    setPlaceId(id);
    setCoordinates(undefined);
  };

  useEffect(() => {
    if (!addressString && AddressInputRef.current) {
      AddressInputRef.current.focus();
    }
  }, [addressString, AddressInputRef]);

  return (
    <DeliveryAddressWrapper>
      <DeliveryAddressInput
        autoFocus
        data-private="redact"
        data-testid="delivery-address-input"
        label={formatMessage({ id: 'deliveryAddress' })}
        matchTypes={['address']}
        message={message}
        onAddressSelected={handleAddressChange}
        onInputValueChange={setValue}
        ref={AddressInputRef}
        required
        setMessage={setMessage}
        value={addressString}
      />
    </DeliveryAddressWrapper>
  );
};

export default DeliveryAddressFormInput;
